import keyBy from 'lodash/keyBy';
import { brandLoader } from './utils/brandLoader';
import { normalizeBrandIds, productsLoader } from './utils/productsLoader';

import { WALLET_CARDS_URL, WALLET_CARDS_ARCHIVED_URL, SECURED_WALLET_URL } from 'common/api/config';
import { withTokensIfNeeded } from 'common/api/utils/withTokensIfNeeded';
import {
    Card,
    CardBalance,
    CardEligibility,
    CardThirdPartyViewerUrl,
} from 'common/api/e-comm/models/Card';
import { uniq } from 'common/utils';
import { APIResponse } from 'common/api/models';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { Product, ProductType } from 'common/api/e-comm/models/Product';
import { RemoteCardDetails } from 'common/api/e-comm/models/RemoteCardDetails';

export const getWallet = () =>
    withTokensIfNeeded<Card[]>(`${WALLET_CARDS_URL}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

export const getArchivedWallet = () =>
    withTokensIfNeeded<Card[]>(`${WALLET_CARDS_ARCHIVED_URL}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

interface UnarchiveCard {
    type: string;
    title: string;
    status: number;
    detail: string;
    instance: string;
}

export const unarchiveCard = (id: string) =>
    withTokensIfNeeded<UnarchiveCard>(`${WALLET_CARDS_ARCHIVED_URL}/${id}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });

export type WalletWithAssociatedBrandsResponse = APIResponse<{
    brands: { [id: string]: ECommBrand };
    cards: { userId: string; cards: { [id: string]: Card } };
    reloadProducts: { [brandId: number]: Product };
    eGiftProducts: { [brandId: number]: Product };
    digitalReloadProducts: { [brandId: number]: Product };
    productsFetchedBrandIds: number[];
}>;

export interface GetWalletOptions {
    // Brands whose products are already in Redux; their product fetch is skipped.
    hydratedBrandIds?: number[];
}

const walletRequestsInFlight = new Map<string, Promise<WalletWithAssociatedBrandsResponse>>();

// `undefined` brandIds loads every wallet brand while `[]` loads none, so they get distinct keys.
const getWalletRequestKey = (userId: string, brandIds?: number[], options: GetWalletOptions = {}) =>
    [
        userId,
        brandIds === undefined ? '*' : normalizeBrandIds(brandIds).join(','),
        normalizeBrandIds(options.hydratedBrandIds ?? []).join(','),
    ].join('|');

const fetchWalletAndAssociatedBrands = async (
    userId: string,
    brandIds?: number[],
    options: GetWalletOptions = {}
): Promise<WalletWithAssociatedBrandsResponse> => {
    const wallet = await getWallet();

    if (wallet.error) {
        return wallet;
    }

    const cards = wallet.data ?? [];
    const brandIdsToLoad = uniq(cards.map((c) => c.brandId));
    const hydratedBrandIds = new Set(options.hydratedBrandIds ?? []);

    // reload or egift
    const filteredBrandIds = brandIdsToLoad.filter(
        (id) => (!brandIds || brandIds.includes(id)) && !hydratedBrandIds.has(id)
    );

    const [batchedBrands, allProductsResults] = await Promise.all([
        brandLoader.loadBrands(brandIds ?? cards.map((c) => c.brandId)),
        productsLoader.loadProducts(filteredBrandIds),
    ]);

    const brands = batchedBrands.reduce<ECommBrand[]>(
        (aggregateBrands, response) =>
            response.error ? aggregateBrands : aggregateBrands.concat(response.data),
        []
    );

    // get reload and egift products
    const allProducts = allProductsResults.map((r) => r.response.data ?? []).flat();

    const reloadProducts: { [brandId: number]: Product } = {};
    const eGiftProducts: { [brandId: number]: Product } = {};
    const digitalReloadProducts: { [brandId: number]: Product } = {};

    for (const product of allProducts) {
        if (product.type === ProductType.Reload) {
            reloadProducts[product.brandId] = product;
        } else if (product.type === ProductType.EGiftCard) {
            eGiftProducts[product.brandId] = product;
        } else if (product.type === ProductType.DigitalReload) {
            digitalReloadProducts[product.brandId] = product;
        }
    }

    return {
        data: {
            brands: keyBy(brands, (brand) => brand.id),
            cards: { userId, cards: keyBy(cards, (card) => card.id) },
            reloadProducts,
            eGiftProducts,
            digitalReloadProducts,
            // Failed brands stay unmarked so the next request retries them.
            productsFetchedBrandIds: allProductsResults
                .filter((r) => !r.response.error)
                .map((r) => r.brandId),
        },
    };
};

export const getWalletAndAssociatedBrands = (
    userId: string,
    brandIds?: number[],
    options?: GetWalletOptions
): Promise<WalletWithAssociatedBrandsResponse> => {
    const key = getWalletRequestKey(userId, brandIds, options);
    const pending = walletRequestsInFlight.get(key);

    if (pending) {
        return pending;
    }

    // Concurrent callers with the same normalized inputs share one wallet request.
    const request = (async () => {
        try {
            return await fetchWalletAndAssociatedBrands(userId, brandIds, options);
        } finally {
            walletRequestsInFlight.delete(key);
        }
    })();
    walletRequestsInFlight.set(key, request);

    return request;
};

export const editCardDetails = (id: string, name: string, pin: string) =>
    withTokensIfNeeded(`${WALLET_CARDS_URL}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name, pin }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

export interface RegisterCardRequest {
    brandId: number;
    cardNumber: string;
    pin: string | null;
    name: string | null;
    supplierCode: string | null;
    productSku: string | null;
}

export const registerCard = (registerCardRequest: RegisterCardRequest) =>
    withTokensIfNeeded<Card>(WALLET_CARDS_URL, {
        method: 'POST',
        body: JSON.stringify(registerCardRequest),
        headers: {
            'Content-Type': 'application/json',
        },
    });

export const updateBalance = (cardId: string, cardBalance: number) =>
    withTokensIfNeeded(`${WALLET_CARDS_URL}/${cardId}/balance`, {
        method: 'PUT',
        body: JSON.stringify({ cardBalance }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

export const getCardBalance = (cardId: string) =>
    withTokensIfNeeded<CardBalance>(`${WALLET_CARDS_URL}/${cardId}/balance`);

export const deleteCard = (cardId: string) =>
    withTokensIfNeeded(`${WALLET_CARDS_URL}/${cardId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
    });

export const getCardBarcode = (cardId: string) =>
    withTokensIfNeeded<string>(`${WALLET_CARDS_URL}/${cardId}/barcode`);

export const setViewedDate = (cardId: string) =>
    withTokensIfNeeded(`${WALLET_CARDS_URL}/${cardId}/view`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });

export const getWalletCardById = (cardId: string) =>
    withTokensIfNeeded<Card>(`${WALLET_CARDS_URL}/${cardId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

export const getThirdPartyViewUrl = async (id: string) => {
    const response = await withTokensIfNeeded<CardThirdPartyViewerUrl>(
        `${SECURED_WALLET_URL}/viewer/view-card/${id}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    return response;
};

export const getRemoteCardDetails = (cardId: string) =>
    withTokensIfNeeded<RemoteCardDetails>(`${WALLET_CARDS_URL}/${cardId}/remote`);

export const validateCard = (cardNumber: string, supplierCode: string) =>
    withTokensIfNeeded<CardEligibility>(`${WALLET_CARDS_URL}/validate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cardNumber, supplierCode }),
    });

export const isPinRequiredForReloadOrBalance = (supplierCode: string) =>
    withTokensIfNeeded<{ isPinRequired: boolean }>(
        `${WALLET_CARDS_URL}/IsPinRequiredForReloadOrBalance`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ supplierCode }),
        }
    );
